/**
 * The pure half of `lib/chat.ts` — what a turn in flight looks like after
 * each event, and the few decisions the chat screen makes about words.
 *
 * Its own file for the reason `ticketRules.ts` is not `tickets.ts`: `chat.ts`
 * imports React Query and the Supabase client, and `supabase.ts` reads
 * `import.meta.env` at load, which does not exist under `node --test`. The
 * Worker's suite is the only one `npm run check` gates on, so anything worth
 * testing lives here, with no runtime import beyond types and `rowMeta.ts`'s
 * dependency-free `berlinDay` and `zone.ts`.
 */
import type { ChatEvent, MessagePart, Proposal } from './chatProtocol'
import { berlinDay } from './rowMeta.ts'
import { clockIn, hereZone } from './zone.ts'
import type { ListKind } from './types'

// ---------------------------------------------------------------------------
// A turn, event by event
// ---------------------------------------------------------------------------

/**
 * One event folded into the parts drawn so far — `partsFromEvents`'
 * semantics, one event at a time, so the screen can redraw as the stream
 * arrives rather than after it:
 *
 * - a `text` delta extends the last part when that part is text, and starts
 *   a new text part otherwise (text after a card row is a new paragraph
 *   below it, not an edit of the one above);
 * - `cards`, `proposal` and `sources` append, a second `cards` included;
 * - everything else — `status`, `usage`, `done`, and `error` — leaves the
 *   parts alone. `error` is state (`TurnState.error`), drawn as a banner
 *   under the turn rather than as a part inside it; the Worker persists its
 *   own `error` part (9.1), and that is what a reload shows.
 *
 * Returns the same array when nothing changed, so a caller holding it in
 * React state does not re-render on a `usage` frame.
 */
export function applyEvent(parts: MessagePart[], e: ChatEvent): MessagePart[] {
  switch (e.type) {
    case 'text': {
      const last = parts[parts.length - 1]
      if (last && last.type === 'text') {
        return [...parts.slice(0, -1), { type: 'text', text: last.text + e.delta }]
      }
      return [...parts, { type: 'text', text: e.delta }]
    }
    case 'cards':
      return [...parts, { type: 'cards', entry_ids: e.entry_ids }]
    case 'proposal':
      return [...parts, { type: 'proposal', id: e.id, action: e.action, outcome: 'offered' }]
    case 'sources':
      return [...parts, { type: 'sources', items: e.items }]
    case 'status':
    case 'thinking':
    case 'usage':
    case 'error':
    case 'done':
      return parts
  }
}

/** Everything `useAsk` draws for the turn in flight. */
export interface TurnState {
  parts: MessagePart[]
  /** The latest `status` label; null once text starts, and after the turn. */
  status: string | null
  error: { message: string; retryable: boolean } | null
  /**
   * The stream ended without `done` and without an `error` of its own — the
   * phone backgrounded, the connection dropped, or Stop was pressed. A flag of
   * its own rather than read back out of `error.message === 'Interrupted'`:
   * the brief fixes `error` to carry that message, and a Worker error that
   * happened to say "Interrupted" would otherwise draw as the wrong state.
   */
  interrupted: boolean
  /** `done`'s `message_id` — the persisted row that replaces this turn. */
  doneId: string | null
  /** WP 9.12 — the thinking summary so far, apart from `parts`: it is drawn
   *  while the turn runs and never persisted, so it is not a part. */
  thinking: string
  /** WP 9.12 — what the turn has done so far, one step per status (`foldTrail`).
   *  Also apart from `parts`: the Worker appends its `trail` part at the end
   *  of the turn, after `partsFromEvents` (9.11's `persist.ts`), so the live
   *  fold and the batch fold still agree part for part. */
  trail: TrailStep[]
}

export const EMPTY_TURN: TurnState = {
  parts: [], status: null, error: null, interrupted: false, doneId: null, thinking: '', trail: [],
}

/**
 * One event folded into the live trail — the same rule the Worker's
 * `trailPart` persists (9.11's `persist.ts`), one event at a time: a
 * `status` adds a step; the next non-empty `cards` or `sources` fills the
 * earliest step that has no detail yet, with the card count ("3 shown") or
 * the sources' hosts. First status, first claim — which is why "no detail
 * yet" is the whole of the state it needs. Same array back when nothing
 * changed.
 */
export function foldTrail(steps: TrailStep[], e: ChatEvent): TrailStep[] {
  if (e.type === 'status') return [...steps, { label: e.label }]
  let detail: string | null = null
  if (e.type === 'cards' && e.entry_ids.length) detail = `${e.entry_ids.length} shown`
  else if (e.type === 'sources' && e.items.length) {
    detail = [...new Set(e.items.map(it => {
      try { return new URL(it.url).host.replace(/^www\./, '') } catch { return it.url }
    }))].join(', ')
  }
  if (detail === null) return steps
  const i = steps.findIndex(s => s.detail === undefined)
  if (i === -1) return steps
  return steps.map((s, j) => (j === i ? { ...s, detail: detail! } : s))
}

export const INTERRUPTED = { message: 'Interrupted', retryable: true } as const

/**
 * a2-07 — why a question failed, as the screen says it. It used to say
 * "Interrupted" whether the phone was offline, the Worker failed or the
 * stream dropped. Doug chose: say why — "No connection" offline, "Ask is
 * down" on a 5xx, the stream's own message when it sent one (that never
 * comes through here: `reduceTurn` keeps it as it arrived), "Interrupted"
 * only for a dropped stream — and keep Ask again.
 *
 * - `offline`: nothing reached the Worker — `navigator.onLine` was false, or
 *   `fetch` itself threw (a network error, not an answer).
 * - `http`: the Worker answered, with an error status before any stream.
 * - `dropped`: the stream ended without `done` or `error`. Offline at that
 *   moment, the likeliest reason is the connection, and it says so.
 */
export type AskFailure =
  | { kind: 'offline' }
  | { kind: 'http'; status: number; detail?: string }
  | { kind: 'dropped'; online: boolean }

export function failureFor(f: AskFailure): { message: string; retryable: boolean } {
  switch (f.kind) {
    case 'offline':
      return { message: 'No connection', retryable: true }
    case 'dropped':
      return f.online ? { ...INTERRUPTED } : { message: 'No connection', retryable: true }
    case 'http': {
      if (f.status === 401) return { message: 'Signed out. Sign in again to ask.', retryable: false }
      // U1.5: profiles.ask_enabled is off for this account (D4: on by default,
      // the admin turns one person off). The Worker answers 403 {error:'ask_disabled'}.
      if (f.status === 403 && f.detail?.includes('ask_disabled')) return { message: "Ask isn't on for your account yet.", retryable: false }
      if (f.status === 429) return { message: 'Too many questions just now. Try again in a minute.', retryable: true }
      if (f.status >= 500) return { message: 'Ask is down', retryable: true }
      const detail = f.detail?.trim().slice(0, 200)
      return { message: detail ? `Ask could not answer (${f.status}): ${detail}` : `Ask could not answer (${f.status}).`, retryable: false }
    }
  }
}

export function reduceTurn(s: TurnState, e: ChatEvent): TurnState {
  const parts = applyEvent(s.parts, e)
  const trail = foldTrail(s.trail, e)
  if (trail !== s.trail) s = { ...s, trail }
  switch (e.type) {
    case 'status':
      // A tool runs between two thinking blocks; a paragraph break keeps
      // the second from running on from the first's last word.
      return {
        ...s, parts, status: e.label,
        thinking: s.thinking && !s.thinking.endsWith('\n\n') ? `${s.thinking}\n\n` : s.thinking,
      }
    case 'thinking':
      return { ...s, thinking: s.thinking + e.delta }
    case 'text':
      return { ...s, parts, status: null }
    case 'error':
      return { ...s, status: null, error: { message: e.message, retryable: e.retryable } }
    case 'done':
      return { ...s, status: null, doneId: e.message_id }
    default:
      return parts === s.parts ? s : { ...s, parts }
  }
}

/** The stream closed. Without `done` and without an error it said itself,
 *  that is an interrupted turn: `{ message: 'Interrupted', retryable: true }`
 *  — or "No connection" when the phone was offline as it closed (a2-07). */
export function endTurn(s: TurnState, online = true): TurnState {
  if (s.doneId || s.error) return { ...s, status: null }
  return { ...s, status: null, error: failureFor({ kind: 'dropped', online }), interrupted: true }
}

// ---------------------------------------------------------------------------
// a2-05 — a cards part whose events have gone
// ---------------------------------------------------------------------------

/** One place in a `cards` part: a live row; a gone event Doug can still
 *  read (its title, and its day where the turn stored one); or, standing
 *  for every gone card with no title stored, one "N events have passed". */
export type CardSlot =
  | { kind: 'row'; id: string }
  | { kind: 'gone'; id: string; title: string; day: string | null }
  | { kind: 'passed'; count: number }

/**
 * Doug chose (a2-05): keep each gone event's title and date, struck
 * through, rather than three identical "No longer listed" boxes. The Worker
 * now stores both beside the ids (`titles`, `firsts`); a message persisted
 * before titles were stored falls back to one line counting them, at the
 * place the first of them was. One persisted with titles but before
 * `firsts` keeps its titles and draws no day — the title is the part that
 * says which event it was.
 *
 * `has(id)` answers whether the row is listed (in the feed or `feed_lite`);
 * the caller asks only once the lookup has settled.
 */
export function cardSlots(
  ids: readonly string[], has: (id: string) => boolean,
  titles: Readonly<Record<string, string>> | undefined, firsts: Readonly<Record<string, string>> | undefined,
): CardSlot[] {
  const out: CardSlot[] = []
  let passed: { kind: 'passed'; count: number } | null = null
  for (const id of ids) {
    if (has(id)) { out.push({ kind: 'row', id }); continue }
    const title = titles && Object.hasOwn(titles, id) ? titles[id]?.trim() : ''
    if (title) {
      out.push({ kind: 'gone', id, title, day: firsts && Object.hasOwn(firsts, id) ? firsts[id] : null })
      continue
    }
    if (passed) passed.count++
    else out.push(passed = { kind: 'passed', count: 1 })
  }
  return out
}

// ---------------------------------------------------------------------------
// The dev-only pass-through to the stub
// ---------------------------------------------------------------------------

const STUB_KEYS = new Set(['fixture', 'pause_after', 'fail'])

/**
 * `?stub=pause_after=2` on the page's own URL → `pause_after=2` for the POST
 * to `scripts/butler-stub.mjs`. Several are joined with `;` or `,`
 * (`?stub=fixture=opera-reviews;pause_after=3`) because a bare `&` would end
 * the `stub` value and start a page parameter of its own. Only the stub's
 * three keys pass; anything else is dropped.
 *
 * `chat.ts` calls this only inside `if (import.meta.env.DEV)`, so a
 * production build drops the call and, with it, this function — the report
 * greps the built bundle for `pause_after` to prove it.
 */
export function stubParams(search: string): string | null {
  const raw = new URLSearchParams(search).get('stub')
  if (raw === null) return null
  const out = new URLSearchParams()
  for (const pair of raw.split(/[;,]/)) {
    const [k, v = ''] = pair.split('=')
    if (STUB_KEYS.has(k)) out.set(k, v)
  }
  return out.toString()
}

// ---------------------------------------------------------------------------
// Threads
// ---------------------------------------------------------------------------

export interface ThreadLike { id: string; title: string | null; last_at: string }

/** a2-02 — the threads the sheet lists: the ones with something in them. A
 *  thread with no messages is a "+" from before the thread was made on the
 *  first send, or a send that failed before the Worker kept the question —
 *  nothing to open, and twenty of them read as twenty identical "Untitled"
 *  lines. A row with no count at all came from a cache persisted before
 *  the count was read (IndexedDB keeps a week): unknown is listed, not
 *  hidden, until the refetch says otherwise. */
export function listedThreads<T extends { messages?: number }>(threads: readonly T[]): T[] {
  return threads.filter(t => (t.messages ?? 1) > 0)
}

/** The day's thread: the most recent one, if it was last used today where
 *  the person is. `threads` arrive `last_at` descending, so only the first can be. */
export function todaysThread<T extends ThreadLike>(threads: readonly T[] | undefined, now: Date): T | null {
  const first = threads?.[0]
  if (!first) return null
  return berlinDay(first.last_at) === berlinDay(now) ? first : null
}

/** A thread's name: its stored title, else the first line Doug wrote, cut
 *  at a word near 60 characters. */
export function threadTitle(stored: string | null, firstUserText: string | null): string | null {
  const raw = (stored ?? firstUserText ?? '').split('\n')[0].trim()
  if (!raw) return null
  if (raw.length <= 60) return raw
  const cut = raw.slice(0, 60)
  const space = cut.lastIndexOf(' ')
  return `${(space > 30 ? cut.slice(0, space) : cut).trimEnd()}…`
}

/** a3-26 (Doug chose: threads titled by their first question). The Worker's
 *  stored title is that question cut to a few words (`What's playing at…`),
 *  which in a list of a dozen reads as a dozen near-twins; the question
 *  itself, cut near 60, is what tells them apart. The stored title is the
 *  fallback, for a thread whose first question has not been read. */
export function listTitle(stored: string | null, firstQuestion: string | null): string | null {
  return threadTitle(null, firstQuestion) ?? threadTitle(stored, null)
}

/** Each thread's first question, from its user rows in any order. */
export function firstQuestions(
  rows: readonly { thread_id: string; created_at: string; parts: readonly MessagePart[] | null }[],
): Map<string, string> {
  const first = new Map<string, { at: string; text: string }>()
  for (const r of rows) {
    const text = userSaid(r.parts)
    if (!text) continue
    const had = first.get(r.thread_id)
    if (!had || r.created_at < had.at) first.set(r.thread_id, { at: r.created_at, text })
  }
  return new Map([...first].map(([id, v]) => [id, v.text]))
}

// ---------------------------------------------------------------------------
// Words on the screen
// ---------------------------------------------------------------------------

/** Lists' own names for its kinds (`Lists.tsx`, `AddToListSheet.tsx`). */
const LIST_LABEL: Record<ListKind, string> = {
  grocery: 'Groceries', task: 'Tasks', buy: 'Purchases', collection: 'Collection', template: 'Checklist',
}

const SCREEN_LABEL: Record<'browse' | 'year' | 'lists' | 'city', string> = {
  browse: 'Browse', year: 'Year', lists: 'Lists', city: 'City',
}

/**
 * What a proposal chip says: the action, a middle dot, the thing —
 * `Save · Tosca`, `Add to Purchases · NordicTrack …`, `Open Browse`.
 * `titleOf` answers an entry id with the row's title, or null when the row
 * is not loaded yet.
 */
export function proposalLabel(p: Proposal, titleOf: (entryId: string) => string | null): string {
  const title = (id: string) => titleOf(id) ?? 'this event'
  switch (p.kind) {
    case 'save': return `Save · ${title(p.entry_id)}`
    case 'schedule': return `Add to calendar · ${title(p.entry_id)}`
    case 'rate': return p.stars ? `Rate ${p.stars} of 5 · ${title(p.entry_id)}` : `Rate · ${title(p.entry_id)}`
    case 'add_item': return `Add to ${LIST_LABEL[p.list_kind] ?? 'list'} · ${p.body}`
    case 'note': return `Note · ${p.body}`
    case 'remember': return `Remember · ${p.body}`
    case 'open':
      return p.screen === 'browse' && p.query?.mode === 'places'
        ? 'Open Places'
        : `Open ${SCREEN_LABEL[p.screen] ?? p.screen}`
    // WP 9.9 — added here, not duplicated in `proposals.ts`'s own wrapper
    // (that file's own note on why `labelFor` stays thin): this switch is
    // the one place a proposal's words live.
    case 'spend_more':
      return `Allow ${Math.round(p.amount_usd * 100)}¢ more today`
    // WP 9.12 — neither is one chip: the question's options and the
    // follow-ups are each their own chip, labelled with their own words.
    // This is what the row of them is called, for a screen reader.
    case 'ask': return p.question
    case 'followups': return 'Ask next'
  }
}

/** The quiet row under a researched answer: one host per source, in order,
 *  `www.` dropped, the same host once. A URL that does not parse is left out
 *  rather than drawn as a broken link. */
export function sourceHosts(items: readonly { url: string; title: string }[]): { host: string; url: string; title: string }[] {
  const seen = new Set<string>()
  const out: { host: string; url: string; title: string }[] = []
  for (const it of items) {
    let host: string
    try { host = new URL(it.url).hostname.replace(/^www\./, '') } catch { continue }
    if (seen.has(host)) continue
    seen.add(host)
    out.push({ host, url: it.url, title: it.title })
  }
  return out
}

// ---------------------------------------------------------------------------
// The trail (WP 9.12) — what a finished turn did, in one line
// ---------------------------------------------------------------------------

/** One step of a turn's trail: a status label as the loop emitted it
 *  ("Looking at Thursday…"), and what it came to — the card count of a find,
 *  the hosts of a search. The shape of `chatProtocol.ts`'s `trail` part. */
export interface TrailStep { label: string; detail?: string }

/** The running label's verb, and what it reads as once it has run. Only the
 *  verbs the Worker's tools actually say (`src/butler/tools/*`'s `label()`
 *  and `run.ts`'s web search); anything else keeps its own words. */
const PAST: Record<string, string> = {
  looking: 'looked', reading: 'read', searching: 'searched', checking: 'checked',
  saving: 'saved', fetching: 'fetched', opening: 'opened', finding: 'found',
}

/** "Looking at Thursday…" → "looked at Thursday"; a verb this does not know
 *  keeps its words, minus the ellipsis. Lower case: the caller capitalises
 *  whichever phrase ends up first. */
export function stepPhrase(label: string): string {
  const bare = label.trim().replace(/(…|\.\.\.)$/, '').trim()
  const m = /^(\p{L}+)(.*)$/u.exec(bare)
  if (!m) return bare
  const verb = m[1].toLowerCase()
  return `${Object.hasOwn(PAST, verb) ? PAST[verb] : verb}${m[2]}`
}

const capital = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s)

/** A step as the trail draws it: adjacent runs of the same step folded into
 *  one, with how many times it ran. */
export interface TrailLine { phrase: string; details: string[]; times: number }

/**
 * The steps, with adjacent repeats merged (a2-06): the loop can call the same
 * tool twice in a row ("Looking at today to Sat 31 Oct…" — once to find,
 * once to page), and the trail drew it as two identical lines and the fold
 * as "Looked 2", which read as two different looks. Equal means the same
 * phrase; the details of the merged steps are kept, each once, in order.
 * Only *adjacent* steps merge — the same look before and after a search is
 * two steps of the story, and the order is what the opened list is for.
 */
export function trailLines(steps: readonly TrailStep[]): TrailLine[] {
  const out: TrailLine[] = []
  for (const s of steps) {
    const phrase = stepPhrase(s.label)
    if (!phrase) continue
    const last = out[out.length - 1]
    if (last && last.phrase === phrase) {
      last.times++
      if (s.detail && !last.details.includes(s.detail)) last.details.push(s.detail)
      continue
    }
    out.push({ phrase, details: s.detail ? [s.detail] : [], times: 1 })
  }
  return out
}

/**
 * The collapsed trail. One step (after `trailLines` merges repeats) keeps its
 * whole phrase — "Looked at tonight". Several are named by verb alone, in the
 * order each verb first appears, a verb that ran more than once counted:
 * "Looked · read 3 · searched". It used to keep each once-run verb's whole
 * phrase too, and three of them — "Read the details · searched the web ·
 * saved what I found" — ran past a 360px phone and ended in an ellipsis
 * (a2-06). The fold says what kind of work was done; the opened list, one
 * tap away, says exactly what. Empty for no steps, so the caller draws
 * nothing.
 */
export function trailSummary(steps: readonly TrailStep[]): string {
  const lines = trailLines(steps)
  if (lines.length === 1) return capital(lines[0].phrase)
  const groups = new Map<string, number>()
  for (const l of lines) {
    const verb = l.phrase.split(/\s/)[0]
    groups.set(verb, (groups.get(verb) ?? 0) + 1)
  }
  const out: string[] = []
  for (const [verb, n] of groups) out.push(n === 1 ? verb : `${verb} ${n}`)
  return capital(out.join(' · '))
}

/** One line of the opened trail: its phrase, capitalised, and how many
 *  times it ran when that was more than once. */
export const stepLine = (l: TrailLine): string =>
  capital(l.phrase) + (l.times === 2 ? ' · twice' : l.times > 2 ? ` · ${l.times} times` : '')

// ---------------------------------------------------------------------------
// Chat manners (WP 9.12)
// ---------------------------------------------------------------------------

/** A message's time, drawn on hover or long-press only: "21:04" today, "Sun
 *  27 Sep 21:04" on any other day — a thread is a day's, but an old one can
 *  be reopened from the threads sheet. The current region's clock and day
 *  (C2.1): a message is the person's, not a venue's. */
export function stampFor(at: string, now: Date): string {
  const d = new Date(at)
  if (Number.isNaN(d.getTime())) return ''
  const tz = hereZone()
  const time = clockIn(d, tz)
  if (berlinDay(d) === berlinDay(now)) return time
  const date = d.toLocaleDateString('en-GB', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short' })
  return `${date.replace(/,/g, '')} ${time}`
}

interface RowLike { id: string; role: 'user' | 'assistant' }

/**
 * Doug's last message and the butler's answer to it — the pair Edit and
 * Retry act on. `answer` is the first assistant row after `question`, or
 * null when there is none yet (still streaming, or the turn never
 * persisted). Both null in a thread Doug has not written in.
 */
export function lastExchange<T extends RowLike>(rows: readonly T[]): { question: T | null; answer: T | null } {
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].role !== 'user') continue
    return { question: rows[i], answer: rows.slice(i + 1).find(r => r.role === 'assistant') ?? null }
  }
  return { question: null, answer: null }
}

/** What Doug wrote in a user row: its text parts, joined. */
export const userSaid = (parts: readonly MessagePart[] | null | undefined): string =>
  (parts ?? []).flatMap(p => (p.type === 'text' ? [p.text] : [])).join('\n').trim()

/**
 * The rows Edit or Retry take out of the thread, read fresh: the last
 * exchange, but only when its question is the one Doug means (`text`).
 * The screen can be a step behind the table — a turn whose `done` has not
 * been refetched yet is drawn from local state and its rows' ids are not
 * known — so the ids come from a re-read, and the text is what says the
 * re-read found the right pair rather than an older one. Null when it did
 * not (nothing persisted yet, or a different last question): then there is
 * nothing of this exchange in the table to take out.
 */
export function exchangeToWithdraw<T extends RowLike & { parts: MessagePart[] | null }>(
  rows: readonly T[], text: string,
): { question: string; answer: string | null } | null {
  const { question, answer } = lastExchange(rows)
  if (!question || userSaid(question.parts) !== text.trim()) return null
  return { question: question.id, answer: answer?.id ?? null }
}
